import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/auth";

/* /smartket/marketers — Public directory of marketer profiles.
   Founders come here to shop for someone who can actually move
   their product. */

export default function SmartKetMarketers() {
  const { user } = useAuth();
  const [marketers, setMarketers] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const prev = document.title;
    document.title = "SmartKet — Marketers Directory";
    return () => { document.title = prev; };
  }, []);

  useEffect(() => { load(); }, []);
  const load = async () => {
    setLoading(true);
    try {
      const params = q.trim() ? { q: q.trim() } : {};
      const r = await axios.get(`${API}/smartket/marketers`, { params });
      setMarketers(r.data?.marketers || []);
    } catch (_e) {}
    setLoading(false);
  };

  return (
    <div data-testid="marketers-page" className="min-h-screen bg-[#0d1410] text-white">
      <div className="border-b-2 border-[#d4a34a]/40 bg-[#141a15]">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-8">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <Link to="/smartket/join" className="font-mono text-[10px] tracking-[0.4em] text-white/60 hover:text-[#d4a34a]">
              ← SMARTKET · JOIN
            </Link>
            {user && (
              <Link to="/smartket/marketer/new" data-testid="edit-my-marketer-btn"
                    className="bg-[#d4a34a] text-black font-display text-sm tracking-widest px-3 py-2 shadow-brutal hover:bg-white">
                + BUILD / EDIT MY PROFILE
              </Link>
            )}
          </div>
          <h1 className="mt-3 font-display text-4xl md:text-5xl leading-none tracking-tight">
            THE <span className="text-[#d4a34a]">MARKETERS</span>
          </h1>
          <p className="mt-3 font-mono text-sm text-white/75 max-w-xl">
            Real people who can actually move product. Skim their reach, their wins, and their niches. Reach out — or wait for them to pull a slip on your listing.
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 md:px-8 py-6">
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex gap-2 mb-6" data-testid="marketers-search-form">
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="search by name, firm, niche…"
                 data-testid="marketers-q" className="flex-1 bg-black/40 border border-white/20 focus:border-[#d4a34a] px-3 py-2 text-white text-sm font-mono outline-none" />
          <button type="submit" data-testid="marketers-search-btn"
                  className="bg-white/10 border border-white/20 hover:bg-white/20 font-mono text-[11px] tracking-widest px-4">
            SEARCH
          </button>
        </form>

        {loading && <div className="font-mono text-sm text-white/60">loading…</div>}

        {!loading && marketers.length === 0 && (
          <div className="border-2 border-dashed border-white/20 p-8 text-center">
            <div className="font-display text-2xl text-white/80 mb-2">No marketers yet.</div>
            <p className="font-mono text-sm text-white/60 max-w-lg mx-auto">
              Be the first. Founders are already listing products — build your profile now and get first pick.
            </p>
            {user && (
              <Link to="/smartket/marketer/new" data-testid="empty-build-btn"
                    className="mt-4 inline-block bg-[#d4a34a] text-black font-display text-sm tracking-widest px-4 py-2 shadow-brutal">
                + BUILD MY PROFILE
              </Link>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {marketers.map(m => (
            <div key={m.id} data-testid={`marketer-${m.id}`}
                 className="border-2 border-white/10 bg-[#141a15] p-5 flex flex-col hover:border-[#d4a34a]/60 transition-colors">
              <div className="flex items-center justify-between mb-2">
                <div className="font-display text-xl leading-tight">{m.display_name}</div>
                {m.slips_active > 0 && (
                  <span className="font-mono text-[10px] tracking-widest text-[#3d7a4f]">
                    {m.slips_active} ACTIVE
                  </span>
                )}
              </div>
              {m.firm_name && (
                <div className="font-mono text-[11px] text-white/60 mb-2">{m.firm_name}</div>
              )}
              <div className="flex flex-wrap gap-1 mb-3">
                {m.niches.slice(0, 5).map(n => (
                  <span key={n} className="font-mono text-[10px] tracking-widest bg-[#d4a34a]/15 text-[#d4a34a] px-1.5 py-0.5">
                    {n.toUpperCase()}
                  </span>
                ))}
              </div>
              <p className="font-mono text-xs text-white/75 leading-relaxed mb-3 flex-1 line-clamp-4">
                {m.reach_description}
              </p>
              <div className="font-mono text-[10px] text-white/50">
                {m.slips_total || 0} slips pulled
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
